"use client";

import { GoogleIcon, Logo } from "@/app/_components/icons";
import { signIn } from "next-auth/react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { SignupFormValues, signupSchema } from "@/app/_lib/schemas/auth";
import { zodResolver } from "@hookform/resolvers/zod";
import Email from "./Email";
import Token from "./Token";
import Password from "./Password";
import Link from "next/link";
import { Loader2 } from "lucide-react";

export default function SignupForm() {
  const [step, setStep] = useState(0);
  const [googleLoading, setGoogleLoading] = useState(false);

  const form = useForm<SignupFormValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      email: "",
      code: "",
      password: "",
      confirmPassword: "",
    },
    mode: "onChange",
  });

  const handleGoogleSignIn = async () => {
    try {
      setGoogleLoading(true);
      await signIn("google");
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center flex-1 px-6 py-16 bg-background min-h-screen">
      <div className="w-full max-w-[380px] flex flex-col gap-7">
        {/* Mobile logo */}
        <Link href="/" className="lg:hidden text-[28px] hover:opacity-80 transition-opacity">
          <Logo />
        </Link>

          <div>
            <h1 className="text-[28px] font-semibold tracking-tight leading-tight">
              {step < 3 ? "Create account" : "Welcome to Busyexpand"}
            </h1>
            <p className="text-muted-foreground mt-1.5 text-[15px]">
              {step === 0 && "Start your journey with us."}
              {step === 1 && "Enter your email to receive a verification code."}
              {step === 2 && "Enter the code we sent to your email."}
              {step === 3 && "One last step: set a password for your account."}
            </p>
          </div>

          {step === 0 && (
            <div className="flex flex-col gap-3">
              <button
                onClick={handleGoogleSignIn}
                disabled={googleLoading}
                className="w-full py-3 flex items-center justify-center gap-3 text-sm border border-border rounded-xl hover:bg-accent transition-colors disabled:opacity-60"
              >
                {googleLoading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Connecting…
                  </>
                ) : (
                  <>
                    <GoogleIcon />
                    Continue with Google
                  </>
                )}
              </button>

              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-border" />
                <span className="text-xs text-muted-foreground">or</span>
                <div className="flex-1 h-px bg-border" />
              </div>

              <button
                onClick={() => setStep(1)}
                className="w-full py-3 bg-primary text-white text-sm font-semibold rounded-xl hover:opacity-90 transition-opacity"
              >
                Continue with Email
              </button>
            </div>
          )}

          {step === 1 && <Email form={form} onNextStep={() => setStep(2)} />}
          {step === 2 && <Token form={form} onNextStep={() => setStep(3)} />}
          {step === 3 && <Password form={form} />}

          <p className="text-center text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link href="/login" className="text-primary font-medium hover:underline">
              Sign in
            </Link>
          </p>
      </div>
    </div>
  );
}
